import React, { Component } from 'react';
import { CardElement, injectStripe } from 'react-stripe-elements';
import {
    withStyles,
    Typography,
    withWidth,
    CircularProgress,
    Icon
} from '@material-ui/core';
import { withRouter } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { red } from '@material-ui/core/colors';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import moment from 'moment';
import ContentHeader from './ContentHeader';
import { formatPrice } from '../helpers/formatters';
import LoaderButton from './LoaderButton';
import {
    DEFAULT_ERROR,
    getFriendlyPaymentError,
    isSafePaymentError
} from '../helpers/errors';
import { getCurrencySymbol } from '../helpers/ticket';
import globalStyles from '../styles';

const styles = {
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        textAlign: 'center'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    card: {
        margin: '30px auto 15px auto',
        maxWidth: '360px',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        padding: '10px 0 10px 8px'
    },
    button: {
        margin: '20px 0 20px',
        width: '200px',
        height: '56px'
    },
    error: {
        margin: '20px 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    saveCard: {
        display: 'inline',
        marginLeft: 14,
        marginTop: 18
    },
    spinner: {
        marginTop: 60,
        marginBottom: 60,
        marginLeft: 'calc(50% - 22px)'
    },
    icon: {
        fontSize: 25
    },
    existingCardParent: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 20
    },
    existingCard: {
        ...globalStyles.savedCard,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 10px',
        flexWrap: 'wrap'
    },
    existingCardHeader: {
        flexBasis: '100%',
        marginTop: 10,
        ...globalStyles.savedCardHeader
    },
    changeCard: {
        width: 'auto',
        height: 'auto',
        padding: '5px',
        lineHeight: '1',
        marginLeft: 10,
        fontSize: 12
    }
};

class TopUpForm extends Component {
    constructor(props) {
        super(props);

        const { ticket } = this.props;
        const numTrips =
            ticket.default_trips > 0 && ticket.default_trips < 50
                ? ticket.default_trips
                : 20;

        this.state = {
            processing: false,
            error: '',
            cardError: '',
            cardValid: false,
            paymentError: false,
            numTrips,
            saveCard: false,
            saveReplacedCard: false,
            paymentMethods: null,
            isLoadingPaymentMethods: true
        };
    }

    async componentDidMount() {
        try {
            // get customer payment methods
            const { client } = this.props;
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const response = await axios.get(
                `${
                    process.env.REACT_APP_PAYMENT_API_ENDPOINT
                }/payment-methods/${
                    currentSession.getAccessToken().payload.sub
                }/${client.slug}`,
                {
                    headers
                }
            );

            // if any, set state
            if (
                response.data &&
                response.data.data &&
                response.data.data.length
            ) {
                this.setState({
                    paymentMethods: response.data.data,
                    isLoadingPaymentMethods: false
                });
            } else {
                this.setState({
                    isLoadingPaymentMethods: false
                });
            }
        } catch (error) {
            console.error(error);
            this.setState({
                isLoadingPaymentMethods: false
            });
        }
    }

    handleSubmit = async () => {
        const { cardError, cardValid, paymentMethods } = this.state;

        if (cardError) {
            this.setState({
                error: cardError
            });
            return;
        }
        if (!cardValid && !paymentMethods) {
            this.setState({
                error: 'Please complete all card details including postcode'
            });
            return;
        }

        this.setState({
            processing: true,
            error: ''
        });

        try {
            await this.handleInitialPayment();
        } catch (e) {
            console.error(e);
            const error = getFriendlyPaymentError(e, DEFAULT_ERROR);
            this.setState({
                processing: false,
                error,
                paymentError: !isSafePaymentError(error)
            });
        }
    };

    handleInitialPayment = async () => {
        const { stripe, history, customerTicket } = this.props;

        const {
            numTrips,
            saveCard,
            saveReplacedCard,
            paymentMethods
        } = this.state;

        let paymentMethodId;

        if (paymentMethods && paymentMethods.length) {
            paymentMethodId = paymentMethods[0].id;
        } else {
            const { paymentMethod, error } = await stripe.createPaymentMethod(
                'card',
                this.cardElement
            );

            if (error) {
                if (error.code === 'card_declined') {
                    throw new Error('Card declined');
                } else if (error.type === 'card_error') {
                    throw new Error('Card error');
                } else {
                    throw new Error('Card api error');
                }
            } else {
                paymentMethodId = paymentMethod.id;
            }
        }

        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.post(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/top-up/stripe`,
            {
                payment_method_id: paymentMethodId,
                customerTicketId: customerTicket.id,
                num_trips: numTrips,
                save_card: saveCard || saveReplacedCard
            },
            {
                headers
            }
        );

        if (response.data.id) {
            const confirmUrl = `/top-up-confirmed/${response.data.id}`;
            history.push(confirmUrl);
            // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
            // resulting in unexpected landing half way down subsequent view.
            window.scrollTo(0, 0);
        } else if (
            response.data.requires_action &&
            response.data.payment_intent_client_secret
        ) {
            await this.handleActionPayment(response.data);
        } else {
            throw new Error('Problem creating payment intent.');
        }
    };

    handleActionPayment = async paymentResponse => {
        const { stripe, history, customerTicket } = this.props;
        const { numTrips } = this.state;

        const {
            error: errorAction,
            paymentIntent
        } = await stripe.handleCardAction(
            paymentResponse.payment_intent_client_secret
        );

        if (errorAction) {
            throw new Error(errorAction.message);
        } else {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const response = await axios.post(
                `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/top-up/stripe`,
                {
                    payment_intent_id: paymentIntent.id,
                    customerTicketId: customerTicket.id,
                    num_trips: numTrips
                },
                {
                    headers
                }
            );

            if (response.data.id) {
                const confirmUrl = `/top-up-confirmed/${response.data.id}`;
                history.push(confirmUrl);
                // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
                // resulting in unexpected landing half way down subsequent view.
                window.scrollTo(0, 0);
            } else if (
                response.data.requires_action &&
                response.data.payment_intent_client_secret
            ) {
                await this.handleAction(response.data);
            } else {
                throw new Error('Problem confirming payment intent.');
            }
        }
    };

    handleReady = element => {
        this.cardElement = element;
        this.cardElement.on('change', response => {
            const cardValid = !response.empty && response.complete;
            const cardError = response.error ? response.error.message : '';
            this.setState({
                cardValid,
                cardError
            });
        });
    };

    handleChange = event => {
        this.setState({
            numTrips: event.target.value
        });
    };

    netPrice = price => {
        return price;
    };

    displayTripOptions = (ticket, numTrips, tripPrice) => {
        const options = [];

        let minTrips = 1;

        // @NOTE: Hardcoded min trips for York college
        if (ticket.client_id === 'b153084a-ac36-4d07-a524-9ce43c9ea3e6') {
            minTrips = 10;

            const now = moment().format('YYYY-MM-DD');

            if (now >= '2023-05-15') {
                minTrips = 1;
            } else if (now >= '2023-04-17') {
                minTrips = 6;
            }
        }

        // @NOTE: Hardcoded min trips for Denso
        if (ticket.client_id === 'd651edf0-3ffe-44cf-8dcc-f93257672c78') {
            minTrips = 5;
        }

        for (let i = minTrips; i <= 50; i += 1) {
            options.push(
                <MenuItem key={`topup-${i}`} value={i}>
                    {this.displayTripOption(ticket, i, tripPrice)}
                </MenuItem>
            );
        }

        return (
            <Select
                id="startTime"
                name="startTime"
                value={numTrips}
                onChange={this.handleChange}
                variant="outlined"
                input={
                    <OutlinedInput
                        name="startTime"
                        labelWidth={0}
                        id="outlined-start-time"
                    />
                }
            >
                {options}
            </Select>
        );
    };

    displayTripOption = (ticket, numTrips, tripPrice) => {
        return `${numTrips} trips x ${getCurrencySymbol(
            ticket.currency
        )}${tripPrice}`;
    };

    handleCheckboxChange = event => {
        this.setState({ [event.target.name]: event.target.checked });
    };

    render() {
        const {
            ticket,
            width,
            customerTicket,
            classes,
            tripPrice
        } = this.props;
        const {
            processing,
            error,
            paymentError,
            numTrips,
            saveCard,
            saveReplacedCard,
            paymentMethods,
            isLoadingPaymentMethods
        } = this.state;

        const hasPaymentMethods = paymentMethods && paymentMethods.length;

        if (isLoadingPaymentMethods) {
            return (
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            );
        }

        return (
            <div className={classes.root}>
                <ContentHeader text="Top-up" id="topup-heading" />
                <div style={{ padding: 20 }}>
                    <div style={{ fontSize: 16 }}>
                        <p>
                            <span style={{ display: 'block' }}>
                                Passenger: {customerTicket.passenger.first_name}{' '}
                                {customerTicket.passenger.last_name}
                            </span>
                            <span style={{ display: 'block' }}>
                                Ticket: {customerTicket.service_name}{' '}
                                {customerTicket.type}
                                {ticket.name ? ` ${ticket.name}` : ''}
                            </span>
                            {customerTicket.zone && (
                                <span style={{ display: 'block' }}>
                                    Zone: {customerTicket.zone}
                                </span>
                            )}
                            {customerTicket.expiry && (
                                <span style={{ display: 'block' }}>
                                    Expiry:{' '}
                                    {moment(customerTicket.expiry).format(
                                        'ddd Do MMM YYYY'
                                    )}
                                </span>
                            )}
                            <span style={{ display: 'block' }}>
                                Balance:{' '}
                                {parseInt(customerTicket.trips, 10) -
                                    parseInt(
                                        customerTicket.trips_taken,
                                        10
                                    )}{' '}
                                trips left
                            </span>
                        </p>
                        <p style={{ marginTop: 20 }}>
                            <strong>Choose top-up amount:</strong>
                        </p>
                        <div style={{ fontSize: 20 }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                                style={{ marginTop: 20 }}
                            >
                                <InputLabel style={{ background: 'white' }}>
                                    Amount
                                </InputLabel>
                                {this.displayTripOptions(
                                    ticket,
                                    numTrips,
                                    tripPrice
                                )}
                            </FormControl>
                            <p>
                                {formatPrice(
                                    this.netPrice(tripPrice * numTrips),
                                    ticket.currency
                                )}
                            </p>
                        </div>
                    </div>

                    <>
                        {!hasPaymentMethods && (
                            <>
                                <CardElement
                                    onReady={this.handleReady}
                                    className={classes.card}
                                    style={{
                                        base: {
                                            fontFamily: '"Roboto"',
                                            fontSize:
                                                width === 'xs'
                                                    ? '14px'
                                                    : '18px',
                                            color: '#000'
                                        }
                                    }}
                                />
                                {!saveReplacedCard && (
                                    <div>
                                        <FormControl
                                            component="fieldset"
                                            className={classes.saveCard}
                                        >
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={saveCard}
                                                            onChange={
                                                                this
                                                                    .handleCheckboxChange
                                                            }
                                                            name="saveCard"
                                                        />
                                                    }
                                                    label="Save card details"
                                                />
                                            </FormGroup>
                                        </FormControl>
                                    </div>
                                )}
                            </>
                        )}
                        {hasPaymentMethods && (
                            <div className={classes.existingCardParent}>
                                <div className={classes.existingCard}>
                                    <div className={classes.existingCardHeader}>
                                        Saved card
                                    </div>
                                    <Icon className={classes.icon}>
                                        credit_card
                                    </Icon>
                                    <span
                                        style={{ marginLeft: 5, fontSize: 16 }}
                                    >
                                        ****-****-****-
                                        {paymentMethods[0].card.last4}
                                    </span>
                                    <LoaderButton
                                        className={`${classes.button} ${classes.changeCard}`}
                                        variant="outlined"
                                        color="primary"
                                        type="submit"
                                        disabled={processing}
                                        text="Change"
                                        onClick={() => {
                                            this.setState({
                                                paymentMethods: null,
                                                saveReplacedCard: true
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </>

                    {error && (
                        <Typography className={classes.error} variant="body2">
                            {error}
                        </Typography>
                    )}

                    <LoaderButton
                        className={`${classes.button} pay-btn`}
                        variant="outlined"
                        color="primary"
                        type="submit"
                        isLoading={processing}
                        disabled={paymentError}
                        text={`Confirm & pay ${formatPrice(
                            this.netPrice(tripPrice * numTrips),
                            ticket.currency
                        )}`}
                        loadingText="Confirming..."
                        onClick={this.handleSubmit}
                    />
                </div>
            </div>
        );
    }
}

export default withStyles(styles)(
    injectStripe(withRouter(withWidth()(TopUpForm)))
);
